// ============================================================
// MarketingHomePage.js
// Public marketing homepage at `/` — the full twelve-module
// platform story. Promoted from /vision 2026-07-11 (the SEO
// ADR's swap condition met: all four Phase-5 modules live); the
// V1 MarketingHomePage is retired. Carries the full page-level
// SEO payload (title/description/canonical/FAQPage JSON-LD) and
// the Slice C UTM query-string forwarding on every /free-trial
// CTA. Filename kept as V2 (rename = cosmetic follow-up).
//
// Self-contained: fixed nav (blur-on-scroll), hero, trust bar,
// pain points, core platform, connected intelligence, operational
// systems, the intelligent-platform centrepiece, how-it-works,
// audiences, CQC section, testimonials, FAQ accordion, bottom CTA,
// footer.
//
// Uses existing design tokens from index.css (brand colours,
// Plus Jakarta Sans display / DM Sans body, spacing, radius) and
// lucide-react icons (inherit colour via currentColor). Dark-surface
// text uses rgba-white, matching the existing MarketingPage.css
// convention for the dark hero. Classes are mhp2__-prefixed to stay
// isolated from V1's mhp__ classes.
//
// "London, UK" in the footer is a permanent project rule.
// ============================================================

import React, { useState, useEffect, createContext, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import {
  Shield, ClipboardList, MapPin, Zap, Headphones,
  Clock, AlertTriangle, FolderOpen, RefreshCw, LayoutDashboard, CalendarClock,
  FileEdit, FileCheck, Bot, BarChart2, ClipboardCheck, Mic, ShieldCheck, Search,
  Home, Building2, Car, Users, Brain, Heart, Briefcase, User,
  Network, BookOpen, Sparkles, FileHeart, PoundSterling, GitMerge, TrendingUp,
  ChevronDown, HeartHandshake, Cloud,
} from 'lucide-react';
import FeatureVideo, { isPrerender } from '../components/marketing/FeatureVideo';
import './MarketingHomePage.css';

const TRUST = [
  { Icon: Shield, label: 'UK Azure Security', to: '/security' },
  { Icon: ClipboardList, label: 'CQC Single Assessment Framework aligned' },
  { Icon: MapPin, label: 'Built for England' },
  { Icon: Zap, label: 'Quick to get started' },
  { Icon: Headphones, label: 'UK-based support team' },
];

const PAIN_POINTS = [
  { Icon: Clock, title: 'Hours lost to documentation every day', body: 'Care staff spending more time writing notes than supporting residents.' },
  { Icon: AlertTriangle, title: 'CQC inspections keep you up at night', body: "Not knowing if your records are inspection-ready until it's too late." },
  { Icon: FolderOpen, title: 'Information scattered across systems', body: 'Care plans, policies and incident records all in different places with no single view.' },
  { Icon: RefreshCw, title: 'Handovers that miss critical information', body: 'Shift changes where things fall through the cracks.' },
  { Icon: LayoutDashboard, title: 'No visibility across multiple locations', body: 'Managing more than one site with no consolidated view of what’s happening.' },
  { Icon: CalendarClock, title: 'Compliance deadlines creeping up on you', body: 'Training renewals, policy reviews and regulatory deadlines managed on spreadsheets.' },
];

const FEATURES = [
  { Icon: FileEdit, name: 'A2Z CareNotes', anchor: 'carenotes', body: 'AI care note quality checking for carers. Type or dictate a note on any device and get structured feedback against CQC criteria in seconds.' },
  { Icon: Bot, name: 'A2Z CareIQ', anchor: 'careiq', body: 'Seven AI agents for Registered Managers, Nominated Individuals and back office staff, covering CQC Inspection Readiness, Governance, Safeguarding, Workforce, Care Planning, Tenders and HR.' },
  { Icon: BarChart2, name: 'A2Z Oversight', anchor: 'oversight', body: 'A live governance dashboard for NIs and RMs. Accidents, Incidents, Complaints, Safeguarding Referrals, Audits and Action Plans, all in one real-time view of your compliance position.' },
  { Icon: ClipboardCheck, name: 'A2Z CareTrack', anchor: 'caretrack', body: 'Capture Accidents, Incidents, Complaints and Safeguarding Referrals as they happen. Native forms on any device, feeding straight into Oversight and CareIQ.' },
  { Icon: Mic, name: 'A2Z CareVoice', anchor: 'carevoice', body: 'An anonymous 6-monthly survey programme for service users, families and carers. It runs automatically each cycle and the results feed into Oversight reporting and CareIQ agent intelligence.' },
  { Icon: ShieldCheck, name: 'A2Z CareComply', anchor: 'carecomply', body: 'Policy distribution, acknowledgement tracking and training compliance management, with staged automatic reminders and a complete, defensible audit trail for every CQC inspection.' },
  { Icon: Search, name: 'A2Z CareAudit', anchor: 'careaudit', body: 'Run audits with CQC-aligned templates for your setting type. Built-in scoring, action plans and three embedded AI agents to help with finding narration, remediation and audit preparation.' },
  { Icon: FileCheck, name: 'A2Z CareEvidence', anchor: 'careevidence', body: 'CQC Quality Statement tagging, evidence portfolio views and an inspection readiness scorecard. Every piece of evidence across the whole platform, mapped to the 34 Quality Statements inspectors use.' },
];

// In-page product shots. Deliberately few: the hero proves the product exists,
// and each of these answers the question the reader is asking at that point.
// Sections without one (the pain points, Getting started, Who it's for, the
// intelligent-platform centrepiece, FAQ) are bare on purpose - an image there
// is page weight without an answer. The catalogue lives on /features.
//
// All lazy. The ONLY eager image on this page is the hero, which is the LCP.
// Homepage videos (2026-09-30): caption-free loops made by
// scripts/marketing/compose-homepage.js. Approved and live the same day.
// They are added after mount and never while prerendering, so the HTML
// crawlers read (and the first paint) keeps the stills. Setting this back
// to false hides them again, leaving them on /?preview=videos for review.
export const HOME_VIDEOS_LIVE = true;
const HOME_VIDEO_DIMS = { phone: { w: 600, h: 1298 }, desktop: { w: 1280, h: 800 } };
const HomeVideosContext = createContext(false);

function HomeShot({ src, alt, w, h, caption, phone, video }) {
  const videos = useContext(HomeVideosContext);
  const dims = HOME_VIDEO_DIMS[phone ? 'phone' : 'desktop'];
  return (
    <figure className={`mhp2__shot${phone ? ' mhp2__shot--phone' : ''}`}>
      {videos && video ? (
        <FeatureVideo className="mhp2__shotvid" id={video} width={dims.w} height={dims.h} alt={alt} showFullscreen={false} />
      ) : (
        <img src={`/features/${src}.webp`} alt={alt} width={w} height={h} loading="lazy" decoding="async" />
      )}
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

const CONNECTED = [
  // Accuracy history: both cards were softened 2026-08-05 while agents only
  // saw document TITLES. Phase 4 (2026-08-10/11) shipped indexed retrieval
  // over CareHub uploads + SharePoint and agents now quote policy content
  // verbatim, so the stronger wording below is the live behaviour.
  // Zuber-approved mock 2026-08-13.
  { Icon: Network, title: 'Your systems, connected', body: 'CareHub connects to your Microsoft 365 tenant and reads the policies and procedures you already hold there. No uploading, no copy and paste.' },
  { Icon: BookOpen, title: 'AI that knows your organisation', body: "CareHub agents don't work from generic templates. They know your live governance data and can quote your own policies word for word, so when an agent helps you prepare for an inspection or look into a governance issue, it works from your organisation rather than a sector average." },
];

const OPERATIONAL = [
  { Icon: FileHeart, name: 'A2Z CarePlan', anchor: 'careplan', body: 'Person-centred care planning and electronic care recording. MAR charts, daily care records, clinical risk management and outcome tracking, replacing the likes of Nourish, PASS and Care Vision on one platform.' },
  { Icon: Users, name: 'A2Z CarePeople', anchor: 'carepeople', body: 'HR and people management for care providers. Employee records, absence management, document distribution and core HR workflows, linked to training compliance and rota scheduling.' },
  { Icon: PoundSterling, name: 'A2Z CareCharge', anchor: 'carecharge', body: 'Provider invoicing and revenue assurance in place of spreadsheets and disconnected billing tools. AI checks pick up underfunded placements, missed rate uplifts and billing anomalies.' },
  { Icon: CalendarClock, name: 'A2Z CareRota', anchor: 'carerota', body: 'Scheduling and rostering built for care. Drag-and-drop shifts with conflict detection, geo check-in for domiciliary visits and payroll-ready hours exports. Completed hours flow straight into CareCharge invoicing.' },
];

const PILLARS = [
  { Icon: GitMerge, title: 'Every module connected', body: 'Data moves across the platform on its own. No exports, no copying between systems. Your governance, compliance, care, HR and financial data form one picture.' },
  { Icon: Brain, title: 'AI agents that talk to each other', body: "CareHub's AI agents don't operate in silos. The inspection readiness agent knows your audit scores, the workforce agent knows your training gaps and the care planning agent knows your governance history. Each one is sharper because the others are working." },
  { Icon: TrendingUp, title: 'Intelligence that compounds', body: 'The more of CareHub you use, the more powerful every part of it becomes. A provider running the full platform has an AI layer that understands their whole operation and keeps getting smarter.' },
];

const STEPS = [
  { title: 'Request your free trial', body: 'Fill in your details and our team will be in touch within one business day to get things started.' },
  { title: 'We set you up', body: 'Our team configures your account, adds your locations and gets everything ready, with no technical work needed from you.' },
  { title: 'Invite your team', body: 'We send invite links to your staff with role-based access already configured. Carers, managers and back office all get exactly what they need.' },
  { title: 'Go live with support', body: 'Your compliance data, governance records and team in one place from day one. Your UK support team is on hand throughout and beyond.' },
];

const AUDIENCES = [
  { Icon: Home, label: 'Residential Care Homes' },
  { Icon: Building2, label: 'Nursing Homes' },
  { Icon: Car, label: 'Domiciliary Care Agencies' },
  { Icon: Users, label: 'Supported Living Providers' },
  { Icon: Brain, label: 'Dementia Care' },
  { Icon: Heart, label: 'Learning Disabilities' },
  { Icon: Briefcase, label: 'Multi-site Providers' },
  { Icon: User, label: 'Independent Providers' },
];

const CQC_CHECKLIST = [
  'Mapped to all five CQC key questions: Safe, Effective, Caring, Responsive and Well-led',
  'Covers all 34 Quality Statements across all modules',
  'Automatically flags documentation gaps before an inspection',
  'AI agents reason over your live governance data to identify risks',
  'Complete audit trails on every record, defensible under inspection',
  'Updated when CQC guidance changes',
];

// Example-widget vocabulary is deliberately the PRODUCT's (evidence
// strength / progress), never CQC's official rating grades — only CQC
// awards "Outstanding"/"Good", and the platform must not imply it
// predicts them.
const CQC_ROWS = [
  { question: 'Safe', rating: 'STRONG EVIDENCE', tone: 'green', note: '14 evidence items this month' },
  { question: 'Effective', rating: 'ON TRACK', tone: 'green', note: 'Care plans reviewed and current' },
  { question: 'Well-led', rating: 'IN PROGRESS', tone: 'blue', note: '2 actions recommended' },
];

// Founder credentials strip — every entry is a verifiable claim.
// Cyber Essentials / NHS DSPT badges are deliberately ABSENT until the
// platform itself holds those certifications. The Microsoft/Google
// entries render logo artwork on white tiles (the standard treatment
// for light-background lockups on a dark surface); assets live in
// public/partners/ (self-hosted — CSP img-src 'self').
const FOUNDER_CREDS = [
  { Icon: HeartHandshake, label: 'Supporting the care sector for 5+ years' },
  { img: '/partners/microsoft-partner.png', alt: 'Microsoft Partner' },
  { img: '/partners/google-g.png', alt: '', label: 'Google Partner' },
  { Icon: Cloud, label: 'Built natively on Microsoft Azure' },
  { Icon: MapPin, label: 'Data stored in UK data centres' },
  { Icon: ShieldCheck, label: 'UK GDPR aligned' },
];

const FAQS = [
  { q: 'Is my data secure and stored in the UK?', a: "Yes. CareHub is built natively on Microsoft Azure and all your data is stored in UK data centres (Azure UK South). We are UK GDPR aligned. AI requests may be processed on Microsoft's global Azure OpenAI infrastructure under UK GDPR transfer safeguards. Your stored data never leaves the UK. Full details are in our Privacy Policy and Sub-processors list." },
  { q: 'How long does it take to get set up?', a: 'Most providers are fully set up and live within a single working day. Our onboarding is guided and does not require any technical knowledge. Our UK support team is on hand throughout.' },
  { q: 'Do I need to train my staff to use it?', a: 'CareHub is designed for frontline care staff, not IT professionals. Most staff are comfortable using it within a few hours. We provide in-app guidance and a UK support team for any questions.' },
  { q: 'Is it aligned to the CQC Single Assessment Framework?', a: 'Yes. CareHub is aligned to the CQC Single Assessment Framework and the 34 Quality Statements within it. We update the platform automatically when CQC guidance changes.' },
  { q: 'Can I use CareHub alongside my existing system?', a: "Yes. CareHub works alongside your existing care management system, so you don't need to replace anything to get started. Most providers run CareHub in parallel with their existing tools at first and find the switch happens naturally as their team adopts it." },
  { q: 'What happens after my free trial ends?', a: 'At the end of your 30-day trial your account simply pauses. Your data is never deleted and there is no auto-charge. We will be in touch before the trial ends to answer any questions.' },
];

// No aria-label on the span: the wrapping <Link> carries the accessible name,
// and aria-label on a role-less <span> is not reliably exposed anyway. Having a
// second, differently-spaced label was what made this confusing to reason about
// in the first place.
function Logo() {
  return (
    <span className="mhp2__logo">
      <span className="mhp2__logo-a2z">A2Z</span>
      <span className="mhp2__logo-carehub">CareHub</span>
    </span>
  );
}

export default function MarketingHomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  // Slice C pattern (2026-07-11): forward the inbound query string on
  // every /free-trial CTA so UTM attribution survives the / →
  // /free-trial hop (client-side Links drop the query string by
  // default — a UTM-tagged landing would otherwise never reach the
  // trial form's URL).
  const { search } = useLocation();
  const freeTrialTo = { pathname: '/free-trial', search };
  const [videos, setVideos] = useState(false);
  useEffect(() => {
    setVideos((HOME_VIDEOS_LIVE || new URLSearchParams(search).get('preview') === 'videos') && !isPrerender());
  }, [search]);

  // Scroll to top on mount — avoids inheriting a scroll position
  // from a React Router navigation.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Nav gains a blurred dark background once the user scrolls.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <HomeVideosContext.Provider value={videos}>
    <div className="mhp2">
      <Helmet>
        <title>Care Management Software for UK Care Providers | A2Z CareHub</title>
        <meta
          name="description"
          content="AI-powered care management software for UK adult social care. Cut documentation time, evidence CQC compliance against the Single Assessment Framework and keep carers focused on the people they support."
        />
        <link rel="canonical" href="https://a2zcarehub.co.uk/" />
        {/* The hero shot is the LCP element. Preloading it lets the browser
            start the fetch before it has parsed down to the <img>. */}
        <link rel="preload" as="image" href="/features/hero-oversight.webp" fetchpriority="high" />
        {/* Per-page Open Graph. index.html carries a global set; these keep the
            homepage share card explicit rather than inherited. */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="A2Z CareHub" />
        <meta property="og:title" content="Care management software for UK care providers" />
        <meta property="og:description" content="One platform for CQC compliance, care planning, eMAR, HR, rotas and invoicing, with AI throughout. Runs inside Microsoft Teams. Free for 30 days." />
        <meta property="og:url" content="https://a2zcarehub.co.uk/" />
        <meta property="og:image" content="https://a2zcarehub.co.uk/og-card-1200x630.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Care management software for UK care providers" />
        <meta name="twitter:description" content="One platform for CQC compliance, care planning, eMAR, HR, rotas and invoicing, with AI throughout. Runs inside Microsoft Teams." />
        <meta name="twitter:image" content="https://a2zcarehub.co.uk/og-card-1200x630.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: f.a,
              },
            })),
          })}
        </script>
      </Helmet>
      {/* ── NAV ───────────────────────────────────────────── */}
      <header className={`mhp2__nav ${scrolled ? 'mhp2__nav--scrolled' : ''}`}>
        <div className="mhp2__nav-inner">
          {/* The wordmark renders as two flush spans, so its text content is
              "A2ZCareHub" with no space. An aria-label of "A2Z CareHub home"
              therefore does NOT contain the visible text, which breaks voice
              control ("click A2ZCareHub" would not match) and fails the
              label-content-name-mismatch audit. Matching the rendered string
              exactly costs nothing and keeps the "home" affordance. */}
          <Link to="/" className="mhp2__nav-brand" aria-label="A2ZCareHub home">
            <Logo />
          </Link>
          <nav className="mhp2__nav-actions">
            <Link to="/features" className="mhp2__nav-login mhp2__nav-login--secondary">Features</Link>
            <Link to="/contact" className="mhp2__nav-login mhp2__nav-login--secondary">Contact</Link>
            {/* 🔴 PLAIN <a>, NOT <Link> — deliberate, do not "tidy" (2026-09-15).
                Microsoft Clarity is gated to the marketing allowlist by a
                LOAD-TIME check in public/index.html. A client-side <Link> to
                /login would keep Clarity running while the user types their
                work email into the sign-in form; a document load makes the
                gate re-evaluate and refuse. This and the footer link below
                are the only client-side routes out of the allowlist.
                Locked by __tests__/clarityScope.test.js. */}
            <a href="/login" className="mhp2__nav-login">Login</a>
            <Link to={freeTrialTo} className="mhp2__btn mhp2__btn--primary mhp2__btn--sm">
              Start Free Trial
            </Link>
          </nav>
        </div>
      </header>

      <main>

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="mhp2__hero">
        <div className="mhp2__hero-glow" aria-hidden="true" />
        <div className="mhp2__hero-grid" aria-hidden="true" />
        <div className="mhp2__hero-inner">
          <span className="mhp2__badge">The complete AI-powered care management platform</span>
          <h1 className="mhp2__hero-title">
            One platform.<br />
            <span className="mhp2__accent">Every module.</span><br />
            Total intelligence.
          </h1>
          <p className="mhp2__hero-sub">
            A2Z CareHub is built to run every part of your care service, from care note
            quality to care planning, HR, rotas and invoicing, with AI built in
            throughout.
          </p>
          <div className="mhp2__hero-cta">
            <Link to={freeTrialTo} className="mhp2__btn mhp2__btn--primary mhp2__btn--lg">
              Start your free trial
            </Link>
            <Link to="/contact" className="mhp2__btn mhp2__btn--ghost mhp2__btn--lg">
              Request a demo
            </Link>
          </div>
          <p className="mhp2__hero-reassure">
            ✓ No credit card required · ✓ Free for 30 days · ✓ Onboarding support included at no extra cost
          </p>

          {/* The hero shot is ABOVE THE FOLD, so it is the LCP element. It loads
              EAGERLY with fetchPriority high and is preloaded in the head above:
              lazy-loading here would delay the very thing Core Web Vitals
              measures. Every other image on this page is lazy, which is the
              right treatment for them and the wrong one for this. */}
          <div className="mhp2__heroshot">
            {/* Each still sits in its own frame, and its video (when shown)
                fades in over it only once it is playing, so the still stays
                the first paint. The videos wait for the page load event. */}
            <div className="mhp2__heroframe mhp2__heroframe--desk">
              <img
                className="mhp2__heroshot-desk"
                src="/features/hero-oversight.webp"
                alt="The A2Z CareHub governance dashboard, showing a status tile for each module."
                width="1200"
                height="750"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
              {videos && <FeatureVideo className="mhp2__herovid" id="home-hero-desktop" width={1280} height={800} alt="The A2Z CareHub governance dashboard, then the rota suggesting cover for a shift, then an invoice." deferUntilLoad showFullscreen={false} />}
            </div>
            <div className="mhp2__heroframe mhp2__heroframe--phone">
              <img
                className="mhp2__heroshot-phone"
                src="/features/hero-phone.webp"
                alt="A carer writing a care note on a phone."
                width="620"
                height="1341"
                loading="eager"
                decoding="async"
              />
              {videos && <FeatureVideo className="mhp2__herovid" id="home-hero-phone" width={600} height={1298} alt="A carer writing a care note on a phone and getting a stronger version back, then the medication round." deferUntilLoad showFullscreen={false} />}
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ─────────────────────────────────────── */}
      <section className="mhp2__trust">
        <div className="mhp2__trust-inner">
          {TRUST.map(({ Icon, label, to }) => (
            to ? (
              <Link key={label} to={to} className="mhp2__trust-item mhp2__trust-item--link">
                <span className="mhp2__trust-icon" aria-hidden="true"><Icon size={20} /></span>
                <span>{label}</span>
              </Link>
            ) : (
              <div key={label} className="mhp2__trust-item">
                <span className="mhp2__trust-icon" aria-hidden="true"><Icon size={20} /></span>
                <span>{label}</span>
              </div>
            )
          ))}
        </div>
      </section>

      {/* ── PAIN POINTS ───────────────────────────────────── */}
      <section className="mhp2__section">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">Sound familiar?</p>
          <h2 className="mhp2__heading">
            Care providers are drowning in admin. It doesn't have to be this way.
          </h2>
          <div className="mhp2__grid mhp2__grid--3">
            {PAIN_POINTS.map(({ Icon, title, body }) => (
              <div key={title} className="mhp2__card">
                <span className="mhp2__card-icon" aria-hidden="true"><Icon size={28} /></span>
                <h3 className="mhp2__card-title">{title}</h3>
                <p className="mhp2__card-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CORE PLATFORM (7 modules) ─────────────────────── */}
      <section id="platform" className="mhp2__section mhp2__section--alt">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">The platform</p>
          <h2 className="mhp2__heading">
            Everything your team needs to run a well-led, CQC-ready service.
          </h2>
          <p className="mhp2__subheading">
            Eight integrated modules covering every aspect of CQC compliance and quality assurance.
          </p>
          <HomeShot
            src="carecomply-matrix" video="home-carecomply" w={1440} h={900}
            alt="The training matrix, showing each member of staff against each training type and whether it has expired."
            caption="Who has read which policy, and whose training lapses next month."
          />
          <div className="mhp2__grid mhp2__grid--modules">
            {FEATURES.map(({ Icon, name, body, anchor }) => (
              <div key={name} className="mhp2__card">
                <span className="mhp2__card-icon" aria-hidden="true"><Icon size={28} /></span>
                <h3 className="mhp2__card-title">{name}</h3>
                <p className="mhp2__card-body">{body}</p>
                {/* The visible text is identical on all twelve cards. A sighted
                    reader disambiguates from the card heading above it, but a
                    screen-reader user browsing the LINKS LIST (NVDA elements
                    list, JAWS links list, VoiceOver rotor) sees twelve
                    identical entries with no way to tell them apart. So the
                    accessible name names its module. 2.4.4 passes either way
                    on context; this is about the links list being usable.
                    The arrow is decoration and is hidden rather than spoken. */}
                <Link
                  to={`/features#${anchor}`}
                  className="mhp2__card-more"
                  aria-label={`See what is included in ${name}`}
                >
                  See what's included <span aria-hidden="true">→</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONNECTED INTELLIGENCE (Phase 3 & 4) ──────────── */}
      <section className="mhp2__section">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">Connected intelligence</p>
          <h2 className="mhp2__heading">
            AI that knows your organisation, not just generic templates.
          </h2>
          <p className="mhp2__subheading">
            CareHub connects to your Microsoft 365 tenant, so every AI agent can read
            your policies and procedures and knows your live governance data.
            Managed clients connect automatically, with no extra setup.
            Organisations on Google Workspace get the same: share your policy drives with
            CareHub and its agents read and quote them too.
          </p>
          <div className="mhp2__grid mhp2__grid--2">
            {CONNECTED.map(({ Icon, title, body }) => (
              <div key={title} className="mhp2__card">
                <span className="mhp2__card-icon" aria-hidden="true"><Icon size={28} /></span>
                <h3 className="mhp2__card-title">{title}</h3>
                <p className="mhp2__card-body">{body}</p>
              </div>
            ))}
          </div>
          <HomeShot
            src="careiq-workspace" video="home-careiq" w={1440} h={1400}
            alt="The CareIQ agent workspace, showing the panel of specialist agents and a conversation in progress."
            caption="Seven specialist agents, aware of your live data."
          />
          <div className="mhp2__highlight">
            <span className="mhp2__highlight-icon" aria-hidden="true"><Sparkles size={24} /></span>
            <div className="mhp2__highlight-text">
              <h3 className="mhp2__highlight-title">The result</h3>
              <p className="mhp2__highlight-body">
                An AI layer that understands your organisation: your setting type, your
                policies, your governance history, your quality position. The more of
                CareHub you use, the more useful every agent becomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── OPERATIONAL SYSTEMS (Phase 5) ─────────────────── */}
      <section className="mhp2__section mhp2__section--alt">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">Operational systems</p>
          <h2 className="mhp2__heading">
            Extend the platform into every corner of your operation.
          </h2>
          <p className="mhp2__subheading">
            Four additional modules that take CareHub beyond CQC compliance and into the
            day-to-day operational systems care providers currently buy from multiple
            separate vendors.
          </p>
          <div className="mhp2__shotpair">
            <HomeShot
              phone src="careplan-mar" video="home-mar" w={620} h={1342}
              alt="The medication round on a phone, listing the day's scheduled doses with their times and whether each has been recorded."
              caption="The round, at the point of care."
            />
            <HomeShot
              src="carerota-conflict" w={1440} h={1080}
              alt="A rota conflict warning listing a double-booking and two Working Time Regulations breaches, each stating its actual figures."
              caption="It warns with real numbers, and lets you proceed with a reason."
            />
          </div>
          <div className="mhp2__grid mhp2__grid--ops">
            {OPERATIONAL.map(({ Icon, name, body, anchor }) => (
              <div key={name} className="mhp2__card">
                <span className="mhp2__card-icon" aria-hidden="true"><Icon size={28} /></span>
                <h3 className="mhp2__card-title">{name}</h3>
                <p className="mhp2__card-body">{body}</p>
                {/* The visible text is identical on all twelve cards. A sighted
                    reader disambiguates from the card heading above it, but a
                    screen-reader user browsing the LINKS LIST (NVDA elements
                    list, JAWS links list, VoiceOver rotor) sees twelve
                    identical entries with no way to tell them apart. So the
                    accessible name names its module. 2.4.4 passes either way
                    on context; this is about the links list being usable.
                    The arrow is decoration and is hidden rather than spoken. */}
                <Link
                  to={`/features#${anchor}`}
                  className="mhp2__card-more"
                  aria-label={`See what is included in ${name}`}
                >
                  See what's included <span aria-hidden="true">→</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTELLIGENT PLATFORM (centrepiece) ────────────── */}
      <section className="mhp2__section mhp2__intelligent">
        <div className="mhp2__intelligent-glow" aria-hidden="true" />
        <div className="mhp2__container mhp2__intelligent-inner">
          <p className="mhp2__eyebrow">The intelligent platform</p>
          <h2 className="mhp2__heading">
            When everything is connected, everything gets smarter.
          </h2>
          <p className="mhp2__subheading">
            Every module in A2Z CareHub feeds every other: governance data informs care
            planning, survey results shape workforce development, audit findings drive
            training compliance and billing patterns can flag care quality risks. It's one
            connected system across your whole organisation, not a bundle of separate
            tools.
          </p>
          <div className="mhp2__pillars">
            {PILLARS.map(({ Icon, title, body }) => (
              <div key={title} className="mhp2__pillar">
                <span className="mhp2__pillar-icon" aria-hidden="true"><Icon size={36} /></span>
                <h3 className="mhp2__pillar-title">{title}</h3>
                <p className="mhp2__pillar-body">{body}</p>
              </div>
            ))}
          </div>
          <div className="mhp2__statement">
            <p className="mhp2__statement-lead">
              One platform. <span className="mhp2__accent">One AI layer.</span>
            </p>
            <p className="mhp2__statement-sub">
              Twelve modules. Complete operational intelligence for UK adult social care.
            </p>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────── */}
      <section id="how" className="mhp2__section">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">Getting started</p>
          <h2 className="mhp2__heading">Up and running in a day.</h2>
          <p className="mhp2__subheading">
            No lengthy implementation. No consultant fees. No disruption to your team.
          </p>
          <div className="mhp2__steps">
            {STEPS.map((s, i) => (
              <div key={s.title} className="mhp2__step">
                <div className="mhp2__step-circle">{i + 1}</div>
                <h3 className="mhp2__step-title">{s.title}</h3>
                <p className="mhp2__step-body">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ──────────────────────────────────── */}
      <section className="mhp2__section mhp2__section--alt">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">Who it's for</p>
          <h2 className="mhp2__heading">Built for every type of adult social care provider.</h2>
          <div className="mhp2__grid mhp2__grid--4">
            {AUDIENCES.map(({ Icon, label }) => (
              <div key={label} className="mhp2__tile">
                <span className="mhp2__tile-icon" aria-hidden="true"><Icon size={32} /></span>
                <span className="mhp2__tile-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CQC SECTION ───────────────────────────────────── */}
      <section className="mhp2__section">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">CQC Compliance</p>
          <h2 className="mhp2__heading">Inspection-ready, every day.</h2>
          <HomeShot
            src="careevidence-scorecard" video="home-careevidence" w={1440} h={1500}
            alt="The readiness scorecard, showing evidence strength against each CQC quality statement."
            caption="The evidence, already organised against the Quality Statements."
          />
          <div className="mhp2__cqc">
            <div className="mhp2__cqc-text">
              <p className="mhp2__card-body mhp2__cqc-lead">
                A2Z CareHub is built around the CQC Single Assessment Framework and the 34
                Quality Statements inspectors use to assess your service. Every module
                captures the evidence inspectors look for as your team works, without
                anyone having to file it.
              </p>
              <ul className="mhp2__checklist">
                {CQC_CHECKLIST.map((item) => (
                  <li key={item}>
                    <span className="mhp2__tick" aria-hidden="true">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mhp2__cqc-card">
              <span className="mhp2__cqc-card-label">Example: your CQC readiness at a glance</span>
              <div className="mhp2__cqc-rows">
                {CQC_ROWS.map((r) => (
                  <div key={r.question} className="mhp2__cqc-row">
                    <span className="mhp2__cqc-q">{r.question}</span>
                    <span className={`mhp2__rating mhp2__rating--${r.tone}`}>{r.rating}</span>
                    <span className="mhp2__cqc-note">{r.note}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO'S BEHIND CAREHUB (founder's note) ─────────── */}
      <section className="mhp2__section mhp2__section--alt">
        <div className="mhp2__container">
          <p className="mhp2__eyebrow">Who's behind CareHub</p>
          <h2 className="mhp2__heading">From the team that runs IT for care providers.</h2>
          <div className="mhp2__founder-card">
            <p>
              A2Z IT is a Microsoft and Google partner supporting businesses across the
              UK. Care is the sector we specialise in, across Domiciliary Care, Care
              Homes and Supported Living. For more than five years we've looked after
              care providers' IT, from their Microsoft 365 to their Service Desk.
            </p>
            <p>
              That work puts us alongside every part of a care business: Nominated
              Individuals, Registered Managers, HR, Care Coordinators and Carers out on
              the floor. We've supported providers through CQC inspections, audits, PQQs
              for Tenders and work with external Consultants. We've helped them get DSPT
              and Cyber Essentials over the line and we've watched{' '}
              <strong>good teams fight the systems they're given or the spreadsheets
              they're working from</strong>, stuck in paperwork, juggling tools that
              don't talk to each other, inventing workarounds just to get the job done.
            </p>
            <p>
              CareHub is built from all of that. Not from a spec sheet. It's how care
              businesses actually run, put into one platform, with AI taking care of the
              admin.
            </p>
            <div className="mhp2__founder-sig">
              <span className="mhp2__founder-name">Zuber Ahmed</span>
              <span className="mhp2__founder-role">Founder &amp; Director, A2Z IT Limited</span>
            </div>
          </div>
          <div className="mhp2__creds">
            {FOUNDER_CREDS.map(({ Icon, img, alt, label }) => (
              img ? (
                <span key={alt || label} className="mhp2__cred mhp2__cred--logo">
                  <img src={img} alt={alt} className="mhp2__cred-img" />
                  {label && <span className="mhp2__cred-logo-label">{label}</span>}
                </span>
              ) : (
                <span key={label} className="mhp2__cred">
                  <Icon size={16} aria-hidden="true" />
                  <span>{label}</span>
                </span>
              )
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────── */}
      <section id="faq" className="mhp2__section">
        <div className="mhp2__container mhp2__container--narrow">
          <p className="mhp2__eyebrow">FAQ</p>
          <h2 className="mhp2__heading">Common questions.</h2>
          <div className="mhp2__faq">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} className={`mhp2__faq-item ${open ? 'mhp2__faq-item--open' : ''}`}>
                  <button
                    type="button"
                    className="mhp2__faq-q"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    <span>{f.q}</span>
                    <ChevronDown size={18} className="mhp2__faq-chevron" aria-hidden="true" />
                  </button>
                  {open && <p className="mhp2__faq-a">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ────────────────────────────────────── */}
      <section className="mhp2__bottom-cta">
        <div className="mhp2__bottom-glow" aria-hidden="true" />
        <div className="mhp2__container mhp2__bottom-inner">
          <h2 className="mhp2__heading">Ready to experience the complete platform?</h2>
          <p className="mhp2__subheading">
            Run your whole operation on one platform, with a UK team behind you.
          </p>
          <Link to={freeTrialTo} className="mhp2__btn mhp2__btn--primary mhp2__btn--lg">
            Start your free trial
          </Link>
          <p className="mhp2__hero-reassure">Free for 30 days · No credit card · Onboarding support included</p>
          <p className="mhp2__cta-phone">
            Prefer to talk? Call{' '}
            <a href="tel:+442046145222" className="mhp2__phone-link">0204 614 5222</a>
            {' '}and ask for the CareHub team.
          </p>
        </div>
      </section>

      </main>

      {/* ── FOOTER ────────────────────────────────────────── */}
      <footer className="mhp2__footer">
        <div className="mhp2__footer-inner">
          <div className="mhp2__footer-brand">
            <Logo />
          </div>
          <nav className="mhp2__footer-links">
            <Link to="/features">Features</Link>
            <Link to="/security">Security</Link>
            <a href="#how">How it works</a>
            <a href="#faq">FAQ</a>
            <Link to="/contact">Contact</Link>
            <a href="https://a2zcarehub.co.uk/privacy/">Privacy Policy</a>
            <a href="https://a2zcarehub.co.uk/terms/">Terms of Use</a>
            <a href="https://a2zcarehub.co.uk/sub-processors/">Sub-processors</a>
            <Link to="/accessibility">Accessibility</Link>
            {/* Plain <a> for the Clarity load-time gate — see the nav link above. */}
            <a href="/login">Login</a>
          </nav>
          <div className="mhp2__footer-phone">
            Call us on{' '}
            <a href="tel:+442046145222" className="mhp2__phone-link">0204 614 5222</a>.
            {' '}You'll reach A2Z IT, the team behind CareHub. Choose the Sales option,
            mention CareHub and we'll take it from there.
          </div>
          <div className="mhp2__footer-copy">
            © 2026 A2Z IT Limited · London, UK ·{' '}
            <a href="https://a2zit.ai" rel="noopener noreferrer">a2zit.ai</a>
            <br />
            Registered in England and Wales · Company No. 16306508 · VAT No. GB489860716
          </div>
        </div>
      </footer>
    </div>
    </HomeVideosContext.Provider>
  );
}
